import React, { useCallback } from 'react';
import { isSameDay, addDays } from 'date-fns';
import { BlackoutDates } from './../../../../containers/VerticalBookingContainer/blackoutDates';
import { isInUnavailableFetchDates } from './../../../../../components/lab/DateRangePicker/useDatePicker/useBlackoutDates/util';
import { useDatePickerI18n } from './../../datePickerI18n';
import { formatDate } from '../../../../../components/lab/utils';
import {
  KEY_ARROW_DOWN,
  KEY_ARROW_LEFT,
  KEY_ARROW_RIGHT,
  KEY_ARROW_UP,
  KEY_ENTER,
  KEY_PAGE_DOWN,
  KEY_PAGE_UP,
  KEY_SPACE
} from '../../helper';
import { nextMonthDateInSameDayWeek, previousMonthDateInSameDayWeek } from './useDay.utils';

interface UseDayProps {
  date: Date;
  focusedDate: Date | null;
  isDateFocused(date: Date): boolean;
  isDateSelected(date: Date): boolean;
  isDateHovered(date: Date): boolean;
  isDateBlocked(date: Date): boolean;
  isFirstOrLastSelectedDate(date: Date): boolean;
  onDateFocus(date: Date): void;
  onDateSelect(date: Date, changeFocus?: boolean): void;
  onDateHover(date: Date): void;
  blackoutDatesIntegration?: boolean;
  fetchBlackoutDates?: BlackoutDates | null;
  focusedInput?: string | null;
  setIsDayFocused?: (value: boolean) => void;
  getMinimumEnabledDate: (date: Date, diff?: number) => Date;

  // Not used anymore
  dayRef?: React.RefObject<HTMLDivElement>;
}

function useDay({
  date,
  focusedDate,
  isDateSelected,
  isDateFocused,
  isFirstOrLastSelectedDate,
  isDateHovered,
  isDateBlocked,
  onDateSelect,
  onDateFocus,
  onDateHover,
  setIsDayFocused,
  blackoutDatesIntegration = false,
  fetchBlackoutDates = {},
  focusedInput = '',
  getMinimumEnabledDate = (date: Date, diff: number = 1) => new Date()
}: UseDayProps) {
  const { format = formatDate } = useDatePickerI18n();
  const onClick = useCallback(() => onDateSelect(date), [date, onDateSelect]);
  const onMouseEnter = useCallback(() => {
    onDateHover(date);
  }, [date, onDateHover]);
  const disabled = isDateBlocked(date) && !isDateHovered(date);

  const onKeyDownFn = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // const getMinimumEnabledDate = (date: Date, diff: number = 1) => {
      //   let lastEnableDay = date;
      //   while (isDateBlocked(lastEnableDay)) {
      //     lastEnableDay = addDays(lastEnableDay, diff);
      //   }
      //   return lastEnableDay;
      // };

      switch (e.keyCode) {
        case KEY_ARROW_RIGHT: {
          e.preventDefault();
          const newDay = addDays(date, 1);
          if (!isDateBlocked(newDay) && !isDateHovered(newDay)) {
            onDateFocus(newDay);
            setIsDayFocused?.(isSameDay(newDay, date));
          } else {
            const lastEnableDay = getMinimumEnabledDate(newDay);
            onDateFocus(lastEnableDay);
            setIsDayFocused?.(isSameDay(lastEnableDay, date));
          }
          break;
        }
        case KEY_ARROW_LEFT: {
          e.preventDefault();
          const newDay = addDays(date, -1);
          if (!isDateBlocked(newDay) && !isDateHovered(newDay)) {
            onDateFocus(newDay);
            setIsDayFocused?.(isSameDay(newDay, date));
          } else {
            const lastEnableDay = getMinimumEnabledDate(newDay, -1);
            onDateFocus(lastEnableDay);
            setIsDayFocused?.(isSameDay(lastEnableDay, date));
          }
          break;
        }
        case KEY_ARROW_UP: {
          e.preventDefault();
          const newDay = addDays(date, -7);
          if (!isDateBlocked(newDay) && !isDateHovered(newDay)) {
            onDateFocus(newDay);
            setIsDayFocused?.(isSameDay(newDay, date));
          } else {
            const lastEnableDay = getMinimumEnabledDate(newDay, -1);
            onDateFocus(lastEnableDay);
            setIsDayFocused?.(isSameDay(lastEnableDay, date));
          }
          break;
        }
        case KEY_ARROW_DOWN: {
          e.preventDefault();
          const newDay = addDays(date, 7);
          if (!isDateBlocked(newDay) && !isDateHovered(newDay)) {
            onDateFocus(newDay);
            setIsDayFocused?.(isSameDay(newDay, date));
          } else {
            const lastEnableDay = getMinimumEnabledDate(newDay);
            onDateFocus(lastEnableDay);
            setIsDayFocused?.(isSameDay(lastEnableDay, date));
          }
          break;
        }
        case KEY_PAGE_DOWN: {
          e.preventDefault();
          const newDate = nextMonthDateInSameDayWeek(
            date,
            (date) => !isDateBlocked(date) || isDateHovered(date)
          );

          if (!isDateBlocked(newDate) && !isDateHovered(newDate)) {
            onDateFocus(newDate);
            setIsDayFocused?.(isSameDay(newDate, date));
          }
          break;
        }
        case KEY_PAGE_UP: {
          e.preventDefault();
          const newDate = previousMonthDateInSameDayWeek(
            date,
            (date) => !isDateBlocked(date) || isDateHovered(date)
          );
          if (!isDateBlocked(newDate) && !isDateHovered(newDate)) {
            onDateFocus(newDate);
            setIsDayFocused?.(isSameDay(newDate, date));
          }
          break;
        }
        case KEY_ENTER:
        case KEY_SPACE: {
          e.preventDefault();
          onDateSelect(date);
          break;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, isDateBlocked, isDateHovered, onDateFocus, onDateSelect, setIsDayFocused]
  );

  return {
    tabIndex: focusedDate === null || isDateFocused(date) ? 0 : -1,
    isSelected: isDateSelected(date),
    isSelectedStartOrEnd: isFirstOrLastSelectedDate(date),
    isSelectedStartOrEndAndBlackout: blackoutDatesIntegration
      ? isInUnavailableFetchDates(fetchBlackoutDates, date, focusedInput, format) &&
        isFirstOrLastSelectedDate(date)
      : false,
    isAMiddleAndBlackout: blackoutDatesIntegration
      ? isInUnavailableFetchDates(fetchBlackoutDates, date, focusedInput, format) &&
        isDateSelected(date) &&
        !isFirstOrLastSelectedDate(date)
      : false,
    isWithinHoverRange: isDateHovered(date),
    disabledDate: disabled,
    onKeyDown: onKeyDownFn,
    onClick: disabled ? () => {} : onClick,
    onMouseEnter
  };
}

export default useDay;
